// RoomRetentionChip — 部屋 retention の owner-only 案内チップ (P1) + 初回 hint (P2)。
// retention-communication-2026-07-14.md / consult 2026-07-14。
//
// 置き場所は共通 chrome `RoomHeader` の `retentionChip` slot (ルーム名の近く)。
// Classic / Modern の両方に一度で効く。全部 **owner-only** (admin_token 保有時のみ)。
//
// P1) 常設の控えめチップ (core):
//   - オーナー (adminToken あり) のとき owner-gated `GET /admin/rooms/:id/retention`
//     を 1 回だけ叩き、tier で出し分ける:
//       - anonymous → 「匿名の部屋・{idleDays}日アクセスがないと消えます」+ タップで設定
//       - password  → 「{idleDays}日アクセスがないと消えます」+ タップで設定 (CTA 弱め)
//       - email     → 「メール登録済・長期保存」の静かな安心表示 (非インタラクティブ)
//   - 本文は未使用 (無アクセス) 基準を明示、title で「最後のアクセスから起算・入室で
//     期限が延びる・tier ごとの削除フロー」を補足 (短い本文だけだと作成起算に誤読される)。
//   - トーンは静か・前向き (「⚠️警告！消えます」にしない)。色は accent(琥珀=認証済
//     オーナー) と衝突しない控えめトーン (地味な bg-inset / text-muted、既存の 🔒 と同系)。
//   - 取得失敗 (network/5xx/未オーナー) 時はチップを **出さない** (黙って諦める)。
//
// P2) 初回だけの非ブロッキング hint (補助):
//   - 匿名オーナーのみ、グローバル 1 回だけ、閉じられるバッジで「登録すると部屋が
//     長く残せます →」。既存 New! バッジの seen フラグ流儀 (localStorage) を再利用。
//   - 非ブロッキング (描画を妨げない) / dismissible。タップで RoomSettingsModal。
//   - clip 回避のため absolute popover ではなくチップ横のインラインバッジで出す
//     (RoomHeader nav は overflow-hidden)。

import { useEffect, useRef, useState } from 'react';

import { readEnv } from '../../../infrastructure/env.js';
import {
  type FetchRoomRetentionInput,
  type FetchRoomRetentionOutcome,
  fetchRoomRetention,
  type RoomRetention,
} from '../../../infrastructure/retention/retentionClient.js';
import {
  createRetentionHintSeenStore,
  type RetentionHintSeenStore,
} from '../../../infrastructure/retentionHintSeen.js';

/** P1 チップの表示内容 (tier からの純粋導出、テスト容易)。null = 非表示。 */
export interface RetentionChipView {
  readonly text: string;
  /**
   * hover 時のフル説明 (tooltip)。短いチップ本文だけだと「作成から N 日」と誤読
   * されうるので、**未使用 (無アクセス) 期間が基準・入室で期限が延びる**ことと
   * tier ごとの削除フローをここで補う。
   */
  readonly title: string;
  /** タップで RoomSettingsModal を開くか (= 登録導線)。email の安心表示は false。 */
  readonly interactive: boolean;
}

/**
 * retention → チップ表示内容 (純粋関数)。数値は必ず server レスポンス (idleDays /
 * graceDays) を埋め込む (web に 7/30 を直書きしない = 陳腐化防止)。本文は「アクセスが
 * ないと消える」と未使用基準を明示し、title でさらに「最後のアクセスから起算・入室で
 * 延長」を補足する。email は非表示にせず静かな安心表示に倒す (もう長期保存なので煽らない)。
 */
export const retentionChipView = (r: RoomRetention): RetentionChipView => {
  const resets = '誰かが入室すると期限は延びます';
  switch (r.tier) {
    case 'anonymous':
      return {
        text: `匿名の部屋・${r.idleDays}日アクセスがないと消えます`,
        title: `最後のアクセスから${r.idleDays}日で自動的に削除されます（${resets}）。タップして登録すると長く残せます。`,
        interactive: true,
      };
    case 'password':
      return {
        text: `${r.idleDays}日アクセスがないと消えます`,
        title: `最後のアクセスから${r.idleDays}日で自動的に削除されます（${resets}）。タップでルーム設定。`,
        interactive: true,
      };
    case 'email':
      return {
        text: 'メール登録済・長期保存',
        title:
          r.graceDays !== null
            ? `最後のアクセスから${r.idleDays}日でメール警告 → さらに${r.graceDays}日後に削除されます（${resets}）。`
            : `最後のアクセスから${r.idleDays}日アクセスがないと削除されます（${resets}）。`,
        interactive: false,
      };
  }
};

export interface RoomRetentionChipProps {
  readonly roomId: string;
  /**
   * owner の admin_token。undefined / 空 → 非オーナー → 何も描かず fetch もしない
   * (owner-only の gate)。
   */
  readonly adminToken?: string;
  /** チップ / hint タップ時に RoomSettingsModal を開く (登録導線)。 */
  readonly onOpenSettings: () => void;
  /** テスト注入: retention 取得。既定は実 fetch。 */
  readonly loadRetention?: (input: FetchRoomRetentionInput) => Promise<FetchRoomRetentionOutcome>;
  /** テスト注入: seen フラグ store。既定は localStorage 実装。 */
  readonly seenStore?: RetentionHintSeenStore;
}

/**
 * owner-only の retention 案内チップ (P1) + 初回 hint (P2)。RoomHeader の
 * `retentionChip` slot に差し込む。非オーナー / 取得失敗時は null を返す。
 */
export const RoomRetentionChip = ({
  roomId,
  adminToken,
  onOpenSettings,
  loadRetention,
  seenStore,
}: RoomRetentionChipProps): React.ReactNode => {
  const isOwner = adminToken !== undefined && adminToken.length > 0;
  const [retention, setRetention] = useState<RoomRetention | null>(null);

  // seen store は mount 時に 1 度だけ生成 (= 描画ごとに作り直さない)。
  const seenRef = useRef<RetentionHintSeenStore | null>(null);
  if (seenRef.current === null) {
    seenRef.current = seenStore ?? createRetentionHintSeenStore();
  }
  // P2 hint を出すかは初期に 1 度だけ捕捉 (= 途中で markSeen しても即消えない)。
  const [hintSeenAtMount] = useState<boolean>(() => seenRef.current?.hasSeen() ?? true);
  const [hintDismissed, setHintDismissed] = useState<boolean>(false);

  // P1: オーナー時のみ retention を 1 回取得。非オーナー / 取得失敗はチップ非表示。
  useEffect(() => {
    if (!isOwner) {
      setRetention(null);
      return;
    }
    const ac = new AbortController();
    const load = loadRetention ?? ((input) => fetchRoomRetention(input));
    void (async () => {
      const out = await load({
        dispatcherUrl: readEnv().dispatcherUrl,
        roomId,
        adminToken,
        signal: ac.signal,
      });
      if (ac.signal.aborted) return;
      // 取得失敗 (unauthorized / not-found / error) は黙ってチップを出さない。
      setRetention(out.outcome === 'ok' ? out.retention : null);
    })();
    return () => ac.abort();
  }, [isOwner, roomId, adminToken, loadRetention]);

  if (!isOwner || retention === null) return null;

  const view = retentionChipView(retention);
  // P2: 匿名オーナー かつ グローバル未 seen かつ 未 dismiss のときだけ hint を出す。
  const showHint = retention.tier === 'anonymous' && !hintSeenAtMount && !hintDismissed;

  const markHintSeen = (): void => {
    setHintDismissed(true);
    seenRef.current?.markSeen();
  };
  const openSettingsFromHint = (): void => {
    markHintSeen();
    onOpenSettings();
  };

  // 控えめトーン: accent(琥珀) と衝突しない地味な bg-inset / text-muted (既存 🔒 と同系)。
  const chipClass =
    'shrink-0 select-none whitespace-nowrap rounded border border-[var(--color-border)] bg-[var(--color-bg-inset)] px-2 py-0.5 text-[10px] text-[var(--color-text-muted)]';

  return (
    <div
      className="flex shrink-0 items-center gap-1"
      data-testid="retention-chip"
      data-manual="keep.retention"
    >
      {view.interactive ? (
        <button
          type="button"
          onClick={onOpenSettings}
          title={view.title}
          className={`${chipClass} hover:bg-[var(--color-bg-soft)] hover:text-[var(--color-text)]`}
        >
          {view.text}
        </button>
      ) : (
        // email の安心表示: 非インタラクティブな静かなラベル (role="note")。
        <span role="note" title={view.title} className={chipClass}>
          {view.text}
        </span>
      )}

      {/* P2 初回 hint: 匿名オーナーへの登録ナッジ。clip 回避でインラインバッジ。
          role="status" (非ブロッキング告知)。タップで設定 / × で dismiss、どちらも
          グローバル seen を記録して以後は出さない。 */}
      {showHint ? (
        <span
          role="status"
          data-testid="retention-hint"
          className="flex shrink-0 items-center gap-1 rounded border border-[var(--color-border)] bg-[var(--color-bg-soft)] px-2 py-0.5 text-[10px] text-[var(--color-text)]"
        >
          <button
            type="button"
            onClick={openSettingsFromHint}
            className="whitespace-nowrap font-bold hover:underline"
          >
            登録すると部屋が長く残せます →
          </button>
          <button
            type="button"
            onClick={markHintSeen}
            aria-label="このお知らせを閉じる"
            title="閉じる"
            className="shrink-0 text-[var(--color-text-muted)] leading-none hover:text-[var(--color-text)]"
          >
            ×
          </button>
        </span>
      ) : null}
    </div>
  );
};
